import { ShippingLabelFormat, SignedDocument } from '@/model';
import {
  getErrorResponse,
  getFlattenedFieldErrors,
  isBadRequest,
} from '@/util/RestErrorUtil';

/**
 * Orders attached shipping labels newest first, so `[0]` is the label to print. Labels without a created date sort last;
 * ties keep their original order.
 */
export const sortShippingLabelsNewestFirst = (
  labels: SignedDocument[],
): SignedDocument[] =>
  [...labels].sort(
    (a, b) =>
      (b.createdDate?.toMillis() ?? 0) - (a.createdDate?.toMillis() ?? 0),
  );

/** Opens a shipping label document already attached to the shipment, as stored. */
export const openAttachedShippingLabel = (
  label: SignedDocument | undefined,
): void => {
  if (!label?.signedUrl || !label.documentName) {
    return;
  }
  const link = document.createElement('a');
  link.href = label.signedUrl;
  link.download = label.documentName;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Hands a generated shipping label to the user. PDFs can open in a new tab (falling back to a download if the popup
 * is blocked); ZPL can't be rendered by the browser, so it is always downloaded for the user's Zebra print tooling.
 */
export const deliverShippingLabel = (
  blob: Blob,
  baseName: string,
  format: ShippingLabelFormat,
  { openInNewTab }: { openInNewTab: boolean },
): void => {
  const isZpl = format === ShippingLabelFormat.ZPL;
  const fileName = `${baseName}.${isZpl ? 'zpl' : 'pdf'}`;
  const file = new File([blob], fileName, {
    type: isZpl ? 'text/plain' : 'application/pdf',
  });
  const url = URL.createObjectURL(file);

  if (openInNewTab && !isZpl) {
    const pdfWindow = window.open();
    if (pdfWindow != null) {
      pdfWindow.location.href = url;
      return;
    }
  }

  const link = document.createElement('a');
  link.href = url;
  link.target = '_new';
  link.download = fileName;
  link.click();
  link.remove();
};

/**
 * Reads the backend's validation message (e.g. ZPL requested for a shipment whose template isn't 4x6) from a failed
 * label download. The body is the `ErrorResponse` for a `ValidationException`, whose `errorDetails.fieldErrors` holds the
 * user-facing messages; the label controllers decode it from the raw `arraybuffer` response before rethrowing.
 */
export const shippingLabelErrorDetail = (
  error: unknown,
): string | undefined => {
  if (!isBadRequest(error)) {
    return undefined;
  }
  const errorResponse = getErrorResponse(error);
  if (errorResponse == null || typeof errorResponse !== 'object') {
    return undefined;
  }
  const messages = getFlattenedFieldErrors(errorResponse).filter(
    (message) => typeof message === 'string' && message.length > 0,
  );
  return messages.length > 0 ? messages.join(' ') : undefined;
};
